import { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
 
const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";
 
interface Hackathon { _id: string; title: string; phases?: { evaluation?: { rounds?: { _id: string; name: string; type: string }[] } }; }
interface Member { _id: string; name: string; email: string; }
interface Panel {
  _id: string;
  name: string;
  scope: "idea_selection" | "round";
  hackathonId: { _id: string; title: string } | string;
  roundId?: string | null;
  roundType?: string | null;
  members: ({ _id: string; name: string } | string)[];
}
 
const hackathonTitle = (p: Panel) => {
  if (!p.hackathonId) return "Unknown Hackathon";
  if (typeof p.hackathonId === "object") return p.hackathonId.title;
  return p.hackathonId;
};
const hackathonId = (p: Panel) => {
  if (!p.hackathonId) return "";
  if (typeof p.hackathonId === "object") return p.hackathonId._id;
  return p.hackathonId;
};
 
export function ManagePanels() {
  const { token } = useAuth();
 
  const [panels, setPanels] = useState<Panel[]>([]);
  const [hackathons, setHackathons] = useState<Hackathon[]>([]);
  const [juryMembers, setJuryMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPanel, setEditingPanel] = useState<Panel | null>(null);
 
  const [form, setForm] = useState({
    hackathonId: "", scope: "idea_selection" as "idea_selection" | "round",
    roundId: "", roundType: "", name: "", memberIds: [] as string[],
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
 
  const selectedHackathon = hackathons.find(h => h._id === form.hackathonId);
  const rounds = selectedHackathon?.phases?.evaluation?.rounds ?? [];
 
  // Fetch panels, hackathons, jury members on mount
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${BASE}/panels`, { headers }).then(r => r.json()),
      fetch(`${BASE}/hackathon-events?limit=100`).then(r => r.json()),
      fetch(`${BASE}/admin/members?role=jury`, { headers }).then(r => r.json()),
    ]).then(([panelData, hackData, juryData]) => {
      setPanels(Array.isArray(panelData) ? panelData : []);
      setHackathons(hackData.items ?? []);
      setJuryMembers(Array.isArray(juryData) ? juryData : []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [token]);
 
  const openCreate = () => {
    setEditingPanel(null);
    setForm({ hackathonId: "", scope: "idea_selection", roundId: "", roundType: "", name: "", memberIds: [] });
    setError("");
    setShowForm(true);
  };
 
  const openEdit = (panel: Panel) => {
    setEditingPanel(panel);
    setForm({
      hackathonId: hackathonId(panel),
      scope: panel.scope,
      roundId: panel.roundId ?? "",
      roundType: panel.roundType ?? "",
      name: panel.name,
      memberIds: panel.members.map(m => typeof m === "object" ? m._id : m),
    });
    setError("");
    setShowForm(true);
  };
 
  const toggleMember = (id: string) => {
    setForm(p => ({
      ...p,
      memberIds: p.memberIds.includes(id) ? p.memberIds.filter(m => m !== id) : [...p.memberIds, id],
    }));
  };
 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.hackathonId || !form.name.trim()) { setError("Hackathon and panel name are required."); return; }
    if (form.scope === "round" && !form.roundId) { setError("Round is required for round panels."); return; }
 
    setSubmitting(true); setError(""); setSuccess("");
    try {
      if (editingPanel) {
        // Update name/roundType
        const res = await fetch(`${BASE}/panels/${editingPanel._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: form.name.trim(), roundType: form.roundType || undefined }),
        });
        if (!res.ok) { const d = await res.json(); setError(d.message ?? "Failed to update panel"); return; }
 
        // Sync members: add new, remove removed
        const oldIds = editingPanel.members.map(m => typeof m === "object" ? m._id : m);
        const toAdd = form.memberIds.filter(id => !oldIds.includes(id));
        const toRemove = oldIds.filter(id => !form.memberIds.includes(id));
        if (toAdd.length) await fetch(`${BASE}/panels/${editingPanel._id}/members/add`, {
          method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ memberIds: toAdd }),
        });
        if (toRemove.length) await fetch(`${BASE}/panels/${editingPanel._id}/members/remove`, {
          method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ memberIds: toRemove }),
        });
 
        // Refresh panels
        const updated = await fetch(`${BASE}/panels`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
        setPanels(Array.isArray(updated) ? updated : []);
        setSuccess("Panel updated successfully.");
      } else {
        const res = await fetch(`${BASE}/panels`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            hackathonId: form.hackathonId,
            scope: form.scope,
            name: form.name.trim(),
            roundId: form.scope === "round" ? form.roundId : undefined,
            roundType: form.scope === "round" ? form.roundType || undefined : undefined,
            members: form.memberIds,
          }),
        });
        const data = await res.json();
        if (!res.ok) { setError(data.message ?? "Failed to create panel"); return; }
        // Refresh
        const updated = await fetch(`${BASE}/panels`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
        setPanels(Array.isArray(updated) ? updated : []);
        setSuccess("Panel created successfully.");
      }
      setTimeout(() => setSuccess(""), 3000);
      setShowForm(false);
    } catch (err: any) {
      setError(err.message ?? "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };
 
  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this panel?")) return;
    try {
      const res = await fetch(`${BASE}/panels/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const d = await res.json(); setError(d.message ?? "Failed to delete"); return; }
      setPanels(prev => prev.filter(p => p._id !== id));
    } catch (err: any) { setError(err.message); }
  };
 
  return (
    <div className="max-w-[1200px] py-[32px] px-[12px]">
      <div className="mb-[32px] flex items-center justify-between">
        <div>
          <h1 className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[42px] text-[#1a2332] text-[28px]">Manage Panels</h1>
          <p className="font-['Inter:Regular',sans-serif] font-normal leading-[21px] text-[#6e7891] text-[14px]">
            Create and manage evaluation panels for idea selection and rounds.
          </p>
        </div>
        <button onClick={showForm && !editingPanel ? () => setShowForm(false) : openCreate}
          className="px-[24px] py-[12px] bg-[#0058ab] text-white rounded-[14px] font-['Inter:Medium',sans-serif] font-medium text-[14px] shadow-[0px_4px_12px_0px_rgba(0,88,171,0.25)] hover:bg-[#004a94] transition-colors">
          {showForm && !editingPanel ? "Cancel" : "Create Panel"}
        </button>
      </div>
 
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-300 text-red-700 rounded-[8px] text-[14px]">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-50 border border-green-300 text-green-700 rounded-[8px] text-[14px]">{success}</div>}
 
      {showForm && !editingPanel && (
        <div className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[32px] mb-[32px]"
          style={{ backgroundImage: "linear-gradient(157.593deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)" }}>
          <h3 className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[18px] mb-[24px]">
            {editingPanel ? "Edit Panel" : "Create New Panel"}
          </h3>
          <form onSubmit={handleSubmit} className="flex flex-col gap-[20px]">
            <div className="grid grid-cols-2 gap-[16px]">
              {/* Hackathon */}
              <div className="flex flex-col gap-[8px]">
                <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Hackathon<span className="text-red-500 ml-1">*</span></label>
                <Select value={form.hackathonId} onValueChange={v => setForm(p => ({ ...p, hackathonId: v, roundId: "", roundType: "" }))} disabled={!!editingPanel}>
                  <SelectTrigger className="bg-white border border-[#e0e4f0] rounded-[8px]"><SelectValue placeholder="Select hackathon" /></SelectTrigger>
                  <SelectContent>
                    {hackathons.length === 0
                      ? <SelectItem value="none" disabled>No hackathons found</SelectItem>
                      : hackathons.map(h => <SelectItem key={h._id} value={h._id}>{h.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
 
              {/* Scope */}
              <div className="flex flex-col gap-[8px]">
                <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Scope<span className="text-red-500 ml-1">*</span></label>
                <Select value={form.scope} onValueChange={v => setForm(p => ({ ...p, scope: v as any, roundId: "", roundType: "" }))} disabled={!!editingPanel}>
                  <SelectTrigger className="bg-white border border-[#e0e4f0] rounded-[8px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="idea_selection">Idea Selection</SelectItem>
                    <SelectItem value="round">Round</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
 
            {/* Round fields */}
            {form.scope === "round" && (
              <div className="grid grid-cols-2 gap-[16px]">
                <div className="flex flex-col gap-[8px]">
                  <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Round<span className="text-red-500 ml-1">*</span></label>
                  <Select value={form.roundId} onValueChange={v => setForm(p => ({ ...p, roundId: v }))} disabled={!!editingPanel}>
                    <SelectTrigger className="bg-white border border-[#e0e4f0] rounded-[8px]"><SelectValue placeholder="Select round" /></SelectTrigger>
                    <SelectContent>
                      {rounds.length === 0
                        ? <SelectItem value="none" disabled>No rounds — select a hackathon first</SelectItem>
                        : rounds.map(r => <SelectItem key={r._id} value={r._id}>{r.name || r.type}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-[8px]">
                  <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Round Type</label>
                  <Input value={form.roundType} onChange={e => setForm(p => ({ ...p, roundType: e.target.value }))}
                    placeholder="e.g. Technical Evaluation" className="border border-[#e0e4f0] rounded-[8px]" />
                </div>
              </div>
            )}
 
            {/* Panel name */}
            <div className="flex flex-col gap-[8px]">
              <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Panel Name<span className="text-red-500 ml-1">*</span></label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Enter panel name" className="border border-[#e0e4f0] rounded-[8px]" required />
            </div>
 
            {/* Members — multi-select via checkboxes */}
            <div className="flex flex-col gap-[8px]">
              <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">
                Panel Members <span className="text-[#6e7891] font-normal">(jury members)</span>
              </label>
              {juryMembers.length === 0 ? (
                <p className="text-[#6e7891] text-[13px]">No jury members found. Add jury members in Add Member section.</p>
              ) : (
                <div className="flex flex-wrap gap-[8px]">
                  {juryMembers.map(m => {
                    const selected = form.memberIds.includes(m._id);
                    return (
                      <button key={m._id} type="button" onClick={() => toggleMember(m._id)}
                        className={`px-[12px] py-[6px] rounded-[8px] text-[13px] font-medium border transition-colors ${selected ? "bg-[#0058ab] text-white border-[#0058ab]" : "bg-white text-[#4a5068] border-[#e4e8ee] hover:border-[#0058ab]"}`}>
                        {m.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
 
            <div className="flex gap-[12px] justify-end pt-[12px]">
              <button type="button" onClick={() => setShowForm(false)}
                className="px-[20px] py-[10px] bg-white border border-[#6e7891] text-[#6e7891] rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#f5f7fb] transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="px-[20px] py-[10px] bg-[#0058ab] text-white rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#004a94] transition-colors disabled:opacity-60">
                {submitting ? "Saving..." : editingPanel ? "Update Panel" : "Create Panel"}
              </button>
            </div>
          </form>
        </div>
      )}
 
      {/* Panels list */}
      <div className="flex flex-col gap-[16px]">
        {loading && <p className="text-[#6e7891] text-[14px]">Loading panels...</p>}
        {!loading && panels.length === 0 && <p className="text-[#6e7891] text-[14px]">No panels created yet.</p>}
        {panels.map(panel => {
          const isEditing = editingPanel?._id === panel._id && showForm;
          return (
            <div key={panel._id}
              className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[24px]"
              style={{ backgroundImage: "linear-gradient(157.593deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)" }}>

              {!isEditing ? (
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-[12px] mb-[8px]">
                      <h3 className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[18px]">{panel.name}</h3>
                      <span className="px-[12px] py-[4px] rounded-[8px] bg-[#0058ab] font-['Inter:Medium',sans-serif] font-medium text-[11px] text-white">
                        {panel.scope === "idea_selection" ? "Idea Selection" : "Round"}
                      </span>
                    </div>
                    <p className="font-['Inter:Regular',sans-serif] text-[#6e7891] text-[13px] mb-[16px]">
                      {hackathonTitle(panel)}{panel.roundType && ` • ${panel.roundType}`}
                    </p>
                    <div>
                      <p className="font-['Inter:Medium',sans-serif] font-medium text-[#8b93a8] text-[11px] tracking-[0.275px] uppercase mb-[8px]">
                        Panel Members ({panel.members.length})
                      </p>
                      <div className="flex gap-[8px] flex-wrap">
                        {panel.members.length === 0
                          ? <span className="text-[#6e7891] text-[13px]">No members assigned</span>
                          : panel.members.map((m, i) => (
                            <span key={i} className="px-[12px] py-[6px] bg-white border border-[#e4e8ee] rounded-[8px] font-['Inter:Regular',sans-serif] text-[#4a5068] text-[13px]">
                              {typeof m === "object" ? m.name : m}
                            </span>
                          ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-[8px] ml-4">
                    <button onClick={() => openEdit(panel)}
                      className="px-[16px] py-[8px] bg-white border border-[#0058ab] text-[#0058ab] rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#f0f5ff] transition-colors">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(panel._id)}
                      className="px-[16px] py-[8px] bg-[#ff816e] text-white rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#ff6b56] transition-colors">
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-[20px]">
                  <div className="flex items-center justify-between">
                    <h3 className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[18px]">Edit Panel</h3>
                    <button type="button" onClick={() => { setShowForm(false); setEditingPanel(null); }} className="text-[#6e7891] hover:text-[#1a2332] transition-colors">
                      <X size={18} />
                    </button>
                  </div>

                  {/* Row 1: Hackathon (read-only) + Scope (read-only) */}
                  <div className="grid grid-cols-2 gap-[16px]">
                    <div className="flex flex-col gap-[8px]">
                      <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Hackathon</label>
                      <div className="px-[12px] py-[8px] bg-[#f5f7fb] border border-[#e0e4f0] rounded-[8px] text-[13px] text-[#6e7891]">{hackathonTitle(panel)}</div>
                    </div>
                    <div className="flex flex-col gap-[8px]">
                      <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Scope</label>
                      <div className="px-[12px] py-[8px] bg-[#f5f7fb] border border-[#e0e4f0] rounded-[8px] text-[13px] text-[#6e7891]">{panel.scope === "idea_selection" ? "Idea Selection" : "Round"}</div>
                    </div>
                  </div>

                  {/* Row 2: Panel Name + Round Type (if round) */}
                  <div className={`grid gap-[16px] ${panel.scope === "round" ? "grid-cols-2" : "grid-cols-1"}`}>
                    <div className="flex flex-col gap-[8px]">
                      <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Panel Name<span className="text-red-500 ml-1">*</span></label>
                      <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="Enter panel name" className="border border-[#e0e4f0] rounded-[8px]" required />
                    </div>
                    {panel.scope === "round" && (
                      <div className="flex flex-col gap-[8px]">
                        <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Round Type</label>
                        <Input value={form.roundType} onChange={e => setForm(p => ({ ...p, roundType: e.target.value }))}
                          placeholder="e.g. Technical Evaluation" className="border border-[#e0e4f0] rounded-[8px]" />
                      </div>
                    )}
                  </div>

                  {/* Row 3: Members */}
                  <div className="flex flex-col gap-[8px]">
                    <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[13px]">Panel Members <span className="text-[#6e7891] font-normal">(jury members)</span></label>
                    {juryMembers.length === 0 ? (
                      <p className="text-[#6e7891] text-[13px]">No jury members found. Add jury members in Add Member section.</p>
                    ) : (
                      <div className="flex flex-wrap gap-[8px]">
                        {juryMembers.map(m => {
                          const sel = form.memberIds.includes(m._id);
                          return (
                            <button key={m._id} type="button" onClick={() => toggleMember(m._id)}
                              className={`px-[12px] py-[6px] rounded-[8px] text-[13px] font-medium border transition-colors ${sel ? "bg-[#0058ab] text-white border-[#0058ab]" : "bg-white text-[#4a5068] border-[#e4e8ee] hover:border-[#0058ab]"}`}>
                              {m.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-[12px] justify-end pt-[4px]">
                    <button type="button" onClick={() => { setShowForm(false); setEditingPanel(null); }}
                      className="px-[20px] py-[10px] bg-white border border-[#6e7891] text-[#6e7891] rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#f5f7fb] transition-colors">
                      Cancel
                    </button>
                    <button type="submit" disabled={submitting}
                      className="px-[20px] py-[10px] bg-[#0058ab] text-white rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#004a94] transition-colors disabled:opacity-60">
                      {submitting ? "Saving..." : "Update Panel"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
 